import { AlertTriangle, Check, Sparkles } from "lucide-react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import type { SegmentListCounts } from "../api/useSegmentListCounts";
import { VACANCY_OVERRIDE } from "../constants";
import { useLeavingItems } from "../hooks/useLeavingItems";
import type { SegmentOverrides } from "../hooks/useSegmentOverrides";
import { useUndoToast } from "../hooks/useUndoToast";
import {
  getIncludedDeltaOnRemove,
  OVERRIDE_STATE,
  removeOverrides,
  revertOverrides,
  type OverrideRow,
} from "../overrides";
import type { SegmentFormValues } from "../schemas";
import { Collapse } from "./Collapse";
import { DelayedPlaceholder } from "./DelayedPlaceholder";
import { FixAlert } from "./FixAlert";
import { OverrideItem } from "./OverrideItem";

import { Badge } from "@/components/ui/v2/badge";
import { Button } from "@/components/ui/v2/button";
import { Separator } from "@/components/ui/v2/separator";
import { Skeleton } from "@/components/ui/v2/skeleton";
import { SEGMENT_TOUR_TARGET } from "@/features/segments/tour/constants";
import { useTourTarget } from "@/features/tours/useTourTarget";
import { cn } from "@/lib/utils/cn";
import { useValueFormatter } from "@/utils/useValueFormatter";

type OverridesBlockProps = {
  readOnly: boolean;
  overrides: SegmentOverrides;
  changeSummary?: string;
  onCountsChange: SegmentListCounts["adjust"];
};

export function OverridesBlock({
  readOnly,
  overrides,
  changeSummary,
  onCountsChange,
}: OverridesBlockProps) {
  const { t } = useTranslation();
  const { format } = useValueFormatter();
  const { watch, setValue, getValues } = useFormContext<SegmentFormValues>();
  const formOverrides = watch("vacancyOverrides");
  const { isLeaving, leave } = useLeavingItems();
  const { rows, affected, redundant } = overrides;
  const exceptionsRef = useTourTarget<HTMLElement>(
    SEGMENT_TOUR_TARGET.EXCEPTIONS,
  );
  const showUndoToast = useUndoToast();

  const remove = (vacancyIds: number[]) => {
    const removedRows = rows.filter((row) =>
      vacancyIds.includes(row.vacancy.vacancyId),
    );
    const includedDelta = getIncludedDeltaOnRemove(removedRows);
    leave(vacancyIds, () => {
      const before = getValues("vacancyOverrides");
      const next = removeOverrides(before, vacancyIds);
      setValue("vacancyOverrides", next, { shouldDirty: true });
      onCountsChange(includedDelta, next);
      showUndoToast({
        message: t("admin.segments.builder.exceptions.exceptionRemoved", {
          count: vacancyIds.length,
        }),
        description:
          removedRows.length === 1
            ? (removedRows[0].vacancy.title ??
              t("admin.segments.builder.vacancies.untitled"))
            : undefined,
        onUndo: () => {
          const restored = revertOverrides(
            getValues("vacancyOverrides"),
            before,
            vacancyIds,
          );
          setValue("vacancyOverrides", restored, { shouldDirty: true });
          onCountsChange(-includedDelta, restored);
        },
      });
    });
  };
  const idsOf = (list: OverrideRow[]) =>
    list.map((row) => row.vacancy.vacancyId);

  const showAffected = !readOnly && affected.length > 0;
  const visibleCount = overrides.isResolved
    ? rows.length
    : formOverrides.length;
  const excludedRows = rows.filter(
    (row) => row.type === VACANCY_OVERRIDE.EXCLUDE,
  );
  const includedRows = rows.filter(
    (row) => row.type === VACANCY_OVERRIDE.INCLUDE,
  );

  return (
    <section
      ref={exceptionsRef}
      className="rounded-lg border bg-card"
      aria-labelledby="segment-overrides-title"
      data-testid="segment-overrides"
    >
      <header className="flex flex-wrap items-center gap-3 border-b px-4 py-4">
        <div className="min-w-0 flex-1">
          <h3 id="segment-overrides-title" className="font-medium">
            {t("admin.segments.builder.exceptions.title")}
          </h3>
          <p className="text-xs text-muted-foreground">
            {t("admin.segments.builder.exceptions.subtitle")}
          </p>
        </div>
        {showAffected && (
          <Badge variant="warning" data-testid="overrides-affected-count">
            {t("admin.segments.builder.exceptions.affectedCount", {
              count: affected.length,
              formatted: format(affected.length, "whole"),
            })}
          </Badge>
        )}
        {!readOnly && redundant.length > 0 && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => remove(idsOf(redundant))}
          >
            <Sparkles />
            {t("admin.segments.builder.exceptions.cleanUp", {
              formatted: format(redundant.length, "whole"),
            })}
          </Button>
        )}
        <Badge variant="info" data-testid="overrides-count">
          {t("admin.segments.builder.exceptions.count", {
            count: visibleCount,
            formatted: format(visibleCount, "whole"),
          })}
        </Badge>
      </header>

      <div
        aria-busy={overrides.isFetching}
        className={cn(
          "flex flex-col gap-4 p-4 transition-opacity duration-300 ease-out",
          overrides.isFetching && !overrides.isLoading && "opacity-75",
        )}
      >
        <Collapse open={showAffected} spacing={4}>
          <FixAlert
            variant="warning"
            icon={AlertTriangle}
            fixIcon={Check}
            title={t("admin.segments.builder.exceptions.bannerTitle", {
              count: affected.length,
              formatted: format(affected.length, "whole"),
            })}
            description={
              changeSummary ??
              t("admin.segments.builder.exceptions.bannerDescription")
            }
            fixLabel={t("admin.segments.builder.exceptions.fixAll", {
              formatted: format(affected.length, "whole"),
            })}
            onFix={() => remove(idsOf(affected))}
          />
        </Collapse>

        <OverrideSection
          title={t("admin.segments.builder.exceptions.manuallyExcluded")}
          emptyText={t("admin.segments.builder.exceptions.noExclusions")}
          rows={excludedRows}
          readOnly={readOnly}
          overrides={overrides}
          isLeaving={isLeaving}
          onRemove={(row) => remove([row.vacancy.vacancyId])}
        />
        <Separator />
        <OverrideSection
          title={t("admin.segments.builder.exceptions.manuallyIncluded")}
          emptyText={t("admin.segments.builder.exceptions.noInclusions")}
          rows={includedRows}
          readOnly={readOnly}
          overrides={overrides}
          isLeaving={isLeaving}
          onRemove={(row) => remove([row.vacancy.vacancyId])}
        />
      </div>
    </section>
  );
}

type OverrideSectionProps = {
  title: string;
  emptyText: string;
  rows: OverrideRow[];
  readOnly: boolean;
  overrides: SegmentOverrides;
  isLeaving: (vacancyId: number) => boolean;
  onRemove: (row: OverrideRow) => void;
};

function OverrideSection({
  title,
  emptyText,
  rows,
  readOnly,
  overrides,
  isLeaving,
  onRemove,
}: OverrideSectionProps) {
  const { t } = useTranslation();
  const { format } = useValueFormatter();

  const renderBody = () => {
    if (overrides.isLoading) {
      return (
        <DelayedPlaceholder>
          <Skeleton className="h-[88px] rounded-lg" />
        </DelayedPlaceholder>
      );
    }
    if (rows.length === 0)
      return <p className="text-sm text-muted-foreground">{emptyText}</p>;
    return (
      <ul className="flex flex-col gap-3">
        {rows.map((row) => (
          <Collapse
            key={row.vacancy.vacancyId}
            as="li"
            spacing={3}
            open={!isLeaving(row.vacancy.vacancyId)}
          >
            <OverrideItem
              row={row}
              readOnly={readOnly}
              isAffected={
                row.state === OVERRIDE_STATE.AFFECTED &&
                !overrides.isKept(row.vacancy.vacancyId)
              }
              onKeep={() => overrides.keep(row.vacancy.vacancyId)}
              onRemove={() => onRemove(row)}
            />
          </Collapse>
        ))}
      </ul>
    );
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <h4 className="text-sm font-medium">{title}</h4>
        <Badge variant="default" size="small" shape="pill">
          {format(rows.length, "whole")}
        </Badge>
        <span className="ml-auto text-xs text-muted-foreground">
          {t("admin.segments.builder.exceptions.setInTab")}
        </span>
      </div>
      {renderBody()}
    </div>
  );
}
